import { type FC, type InputHTMLAttributes, type Ref } from "react";
import { FieldError } from "react-hook-form";
import { PhoneInput, PhoneInputProps, PhoneInputRefType } from "react-international-phone";
import { cn } from "../../lib/utils";
import { FormFieldError } from "../FormFieldError";
import { inputSize } from "./inputSize";

type PhoneNumberInputProps = PhoneInputProps & {
	compact?: boolean;
	error?: FieldError;
	errorMessage?: string;
	readonly?: boolean;
	ref?: Ref<PhoneInputRefType>;
};

const autofillIgnoreAttrs = {
	"data-1p-ignore": true,
	"data-lpignore": true,
} as InputHTMLAttributes<HTMLInputElement>;

export const PhoneNumberInput: FC<PhoneNumberInputProps> = ({
	ref,
	compact,
	error,
	errorMessage,
	readonly,
	className,
	disabled,
	inputProps,
	...props
}) => {
	const { frame, paddingX, paddingY } = inputSize(compact);
	return (
		<div className="relative flex w-full h-fit">
			<PhoneInput
				ref={ref}
				{...props}
				disabled={disabled}
				hideDropdown
				disableDialCodeAndPrefix
				showDisabledDialCodeAndPrefix
				dialCodePreviewStyleProps={{
					className: cn("h-full flex items-center text-tertiary", paddingX, "pr-[8px]"),
				}}
				inputProps={{
					...inputProps,
					readOnly: readonly,
					autoComplete: "one-time-code",
					...autofillIgnoreAttrs,
				}}
				className={cn(
					"relative flex shrink-0 w-full rounded-md outline outline-[1px] outline-offset-[-1px] text-primary outline-gray-300 bg-gray-50 bg-opacity-70 placeholder:text-tertiary focus-within:outline-[2px] focus-within:outline-gray-600 focus-within:hover:outline-gray-600",
					frame,
					"font-normal",
					disabled ? "cursor-default opacity-50" : "hover:outline-gray-500",
					className,
					readonly ? "bg-surface-neutral/70 bg-opacity-100 opacity-100" : undefined,
					error
						? "outline-danger focus-within:outline-danger focus-within:hover:outline-danger hover:outline-danger hover:outline-2"
						: undefined,
				)}
				inputClassName={cn("w-full outline-none bg-transparent", paddingX, paddingY, "pl-0")}
			/>
			<FormFieldError error={error} errorMessage={errorMessage} />
		</div>
	);
};
